import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm, CompactRichTextEditor, UserPicker } from '../../../shared/components/common';
import { useCreateWorkItem, useTransitionWorkItemStatus } from '../hooks/useWorkItemQueries';
import { useModules } from '../hooks/useModuleQueries';
import { ModulePicker } from './ModulePicker';
import { InitiativePicker } from './InitiativePicker';
import { settingsApi } from '../services/softwareFactoryApi';
import type { SfWorkItem } from '../types';
import autonomyLevelsConfig from '../../../shared-config/sf_autonomy_levels.config.json';
import dispatchProvidersConfig from '../../../shared-config/sf_dispatch_providers.config.json';
import {
  dispatchProviderIds,
  dispatchModelIds,
  dispatchProviderLabel,
  dispatchModelLabel,
} from '../utils/dispatchProviders';
import { WORK_ITEM_TYPE_VALUES as WORK_ITEM_TYPES } from '../../../generated/config-enums';

const AUTONOMY_LEVELS = Object.keys(autonomyLevelsConfig) as Array<keyof typeof autonomyLevelsConfig>;
const PROVIDER_IDS = dispatchProviderIds(dispatchProvidersConfig);

interface WorkItemQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  defaultModuleId?: string;
  /**
   * Pre-select a module by slug when the caller only knows the slug (e.g. a
   * sysadmin alert suggesting its owning module). Ignored when
   * `defaultModuleId` is given, and silently skipped when the tenant has no
   * module with that slug.
   */
  defaultModuleSlug?: string;
  defaultType?: string;
  /** Pre-select an autonomy level (key of sf_autonomy_levels.config.json). */
  defaultAutonomyLevel?: string;
  defaultInitiativeId?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  onCreated?: (workItem: SfWorkItem) => void;
}

interface QuickCreateFormData {
  title: string;
  type: string;
  module_id: string;
  priority: string;
  description: string;
  size: string;
  autonomy_level: string;
  preferred_provider: string;
  preferred_model: string;
  initiative_id: string;
  assignee_user_id: string;
}

const PRIORITIES = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
] as const;

export function WorkItemQuickCreateDialog({
  open,
  onClose,
  defaultModuleId,
  defaultModuleSlug,
  defaultType,
  defaultAutonomyLevel,
  defaultInitiativeId,
  defaultTitle,
  defaultDescription,
  onCreated,
}: WorkItemQuickCreateDialogProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateWorkItem();
  const transitionMutation = useTransitionWorkItemStatus();
  const skipToPlannedRef = useRef(false);
  // Slug→id resolution needs the module list, which may land after the dialog
  // opens; this guards the late fill so it never overwrites an operator's pick.
  const slugDefaultAppliedRef = useRef(false);

  const { data: modulesData } = useModules();
  const slugDefaultModuleId = useMemo(() => {
    if (defaultModuleId || !defaultModuleSlug) return '';
    return modulesData?.items.find((m) => m.slug === defaultModuleSlug)?.id ?? '';
  }, [defaultModuleId, defaultModuleSlug, modulesData?.items]);

  const { data: workItemSizes } = useQuery({
    queryKey: ['software-factory', 'work-item-sizes'],
    queryFn: settingsApi.getWorkItemSizes,
    staleTime: 5 * 60 * 1000,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      title: defaultTitle || '',
      type: defaultType || 'feature',
      module_id: defaultModuleId || '',
      priority: '2',
      description: defaultDescription || '',
      size: '',
      autonomy_level: defaultAutonomyLevel || '',
      preferred_provider: '',
      preferred_model: '',
      initiative_id: defaultInitiativeId || '',
      assignee_user_id: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: defaultTitle || '',
        type: defaultType || 'feature',
        module_id: defaultModuleId || '',
        priority: '2',
        description: defaultDescription || '',
        size: '',
        autonomy_level: defaultAutonomyLevel || '',
        initiative_id: defaultInitiativeId || '',
        assignee_user_id: '',
      });
      skipToPlannedRef.current = false;
      slugDefaultAppliedRef.current = false;
    }
  }, [
    open,
    reset,
    defaultModuleId,
    defaultModuleSlug,
    defaultType,
    defaultAutonomyLevel,
    defaultInitiativeId,
    defaultTitle,
    defaultDescription,
  ]);

  useEffect(() => {
    if (!open || slugDefaultAppliedRef.current || !slugDefaultModuleId) return;
    // Never overwrite a module the operator already picked while the list loaded.
    if (getValues('module_id')) return;
    setValue('module_id', slugDefaultModuleId);
    slugDefaultAppliedRef.current = true;
  }, [open, slugDefaultModuleId, setValue, getValues]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const skipToPlanned = skipToPlannedRef.current;
    try {
      const newWorkItem = await createMutation.mutateAsync({
        title: data.title,
        type: data.type,
        module_id: data.module_id || undefined,
        priority: parseInt(data.priority, 10),
        description: data.description || undefined,
        size: data.size || undefined,
        autonomy_level: (data.autonomy_level || null) as SfWorkItem['autonomy_level'],
        preferred_provider: (data.preferred_provider || null) as SfWorkItem['preferred_provider'],
        preferred_model: data.preferred_model || null,
        initiative_id: data.initiative_id || undefined,
        assignee_user_id: data.assignee_user_id || undefined,
      });

      if (skipToPlanned) {
        // Transition new → triaged → planned
        await transitionMutation.mutateAsync({
          id: newWorkItem.id,
          data: { status: 'triaged' },
        });
        await transitionMutation.mutateAsync({
          id: newWorkItem.id,
          data: { status: 'planned' },
        });
      }

      onCreated?.(newWorkItem);
      onClose();
      navigate(`/software-factory/work-items/${newWorkItem.id}`);
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const handleSkipToPlanned = () => {
    skipToPlannedRef.current = true;
    handleSubmit(onSubmit)();
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('softwareFactory:workItems.create')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      maxWidth="sm"
      hideActions
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">{t('softwareFactory:workItems.titleField')} *</Label>
          <Controller
            name="title"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                autoFocus
                className={cn(errors.title && 'border-destructive')}
              />
            )}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('softwareFactory:workItems.type')} *</Label>
          <Controller
            name="type"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORK_ITEM_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`softwareFactory:types.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <Controller
          name="module_id"
          control={control}
          render={({ field }) => (
            <ModulePicker
              value={field.value || null}
              onChange={(id) => field.onChange(id || '')}
              label={t('softwareFactory:workItems.module')}
            />
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.priority')}</Label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {t(`softwareFactory:priorities.${p.label}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {workItemSizes && workItemSizes.length > 0 && (
            <div className="space-y-2">
              <Label>{t('softwareFactory:workItems.size')}</Label>
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent>
                      {workItemSizes.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('softwareFactory:workItems.autonomyLevel')}</Label>
          <Controller
            name="autonomy_level"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('softwareFactory:workItems.autonomyLevelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {AUTONOMY_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      <div className="flex flex-col">
                        <span>{t(`softwareFactory:autonomyLevels.${level}.label`)}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(`softwareFactory:autonomyLevels.${level}.description`)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.preferredProvider')}</Label>
            <Controller
              name="preferred_provider"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Model options depend on provider — drop a now-invalid model.
                    setValue('preferred_model', '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('softwareFactory:workItems.preferredProviderPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_IDS.map((provider) => (
                      <SelectItem key={provider} value={provider}>
                        {dispatchProviderLabel(dispatchProvidersConfig, provider)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('softwareFactory:workItems.preferredModel')}</Label>
            <Controller
              name="preferred_model"
              control={control}
              render={({ field }) => {
                const provider = watch('preferred_provider');
                const modelIds = dispatchModelIds(dispatchProvidersConfig, provider);
                return (
                  <Select value={field.value} onValueChange={field.onChange} disabled={!provider}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          provider
                            ? t('softwareFactory:workItems.preferredModelPlaceholder')
                            : t('softwareFactory:workItems.preferredModelNoProvider')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {modelIds.map((model) => (
                        <SelectItem key={model} value={model}>
                          {dispatchModelLabel(dispatchProvidersConfig, provider, model)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </div>
        </div>

        <Controller
          name="initiative_id"
          control={control}
          render={({ field }) => (
            <InitiativePicker
              value={field.value || null}
              onChange={(id) => field.onChange(id || '')}
              label={t('softwareFactory:initiatives.entity')}
            />
          )}
        />

        <Controller
          name="assignee_user_id"
          control={control}
          render={({ field }) => (
            <UserPicker
              value={field.value || null}
              onChange={(id) => field.onChange(id || '')}
              label={t('softwareFactory:workItems.assignee')}
              filterUserTypes={['internal', 'ai_agent']}
            />
          )}
        />

        <div className="space-y-2">
          <Label>{t('softwareFactory:workItems.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <CompactRichTextEditor
                value={field.value}
                onChange={field.onChange}
                minHeight={80}
              />
            )}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={handleSkipToPlanned}
          disabled={isSubmitting}
          className="px-0 text-muted-foreground"
        >
          {isSubmitting && skipToPlannedRef.current && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          {t('softwareFactory:workItems.createSkipToPlanned')}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('common:app.cancel')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            {isSubmitting && !skipToPlannedRef.current && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t('common:app.create')}
          </Button>
        </div>
      </div>
    </DialogForm>
  );
}
